import React, { useState, useEffect } from 'react';
import { X, Printer, User, ExternalLink, Package, Pencil, CheckCircle2, Circle, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { format, parseISO } from 'date-fns';
import DocumentPreview from '../documents/DocumentPreview';
import OrderModal from './OrderModal';

const STATUS_COLORS = { new: '#F59E0B', sent: '#3B82F6', completed: '#10B981' };

export default function OrderDetailModal({ orderId, onClose, onStatusChanged }) {
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusSaving, setStatusSaving] = useState(false);
  const [paidSaving, setPaidSaving] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [printPreview, setPrintPreview] = useState(null);
  const [showEdit, setShowEdit] = useState(false);

  function loadOrder() {
    setLoading(true);
    api.get(`/orders/${orderId}`)
      .then((r) => setOrder(r.data))
      .catch(() => alert('שגיאה בטעינת ההזמנה'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadOrder(); }, [orderId]); // eslint-disable-line

  async function handleStatusChange(newStatus) {
    setStatusSaving(true);
    try {
      await api.put(`/orders/${orderId}/status`, { status: newStatus });
      setOrder((o) => ({ ...o, status: newStatus }));
      onStatusChanged?.();
    } catch {
      alert('שגיאה בעדכון סטטוס');
    } finally {
      setStatusSaving(false);
    }
  }

  async function togglePaid() {
    if (!order) return;
    const newVal = !order.is_paid;
    setPaidSaving(true);
    try {
      await api.put(`/orders/${orderId}/paid`, { is_paid: newVal });
      setOrder((o) => ({ ...o, is_paid: newVal }));
      onStatusChanged?.();
    } catch {
      alert('שגיאה בעדכון תשלום');
    } finally {
      setPaidSaving(false);
    }
  }

  async function handlePrint() {
    if (!order) return;
    setPrintLoading(true);
    try {
      const templatesRes = await api.get('/documents/templates');
      const tpl = templatesRes.data.find((t) => t.is_default_order) || templatesRes.data[0];
      if (!tpl) {
        alert('לא נמצאה תבנית הזמנה. צור תבנית בעמוד המסמכים.');
        return;
      }
      const renderRes = await api.post('/documents/render', {
        template_id: tpl.id,
        customer_id: order.customer_id,
        order_id: order.id,
      });
      setPrintPreview({
        rendered_html: renderRes.data.html,
        template_name: `הזמנה #${order.order_number}`,
        customer_name: order.customer_name,
      });
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה ביצירת מסמך');
    } finally {
      setPrintLoading(false);
    }
  }

  const items = (() => {
    try {
      if (!order?.items) return [];
      return Array.isArray(order.items) ? order.items : JSON.parse(order.items);
    } catch { return []; }
  })();

  const computedTotal = items.reduce((sum, item) =>
    sum + (parseFloat(item.price) || 0) * (parseInt(item.quantity) || 1), 0);

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" dir="rtl">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
            <div>
              {loading
                ? <div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                : <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                    הזמנה #{order?.order_number}
                  </h2>
              }
              {order && (
                <p className="text-xs text-gray-400 mt-0.5">
                  {format(parseISO(order.created_at), 'dd/MM/yyyy HH:mm')}
                  {order.created_by_name && ` · נפתחה ע"י ${order.created_by_name}`}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {order && (
                <button
                  onClick={() => setShowEdit(true)}
                  title="עריכת הזמנה"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium transition-colors"
                >
                  <Pencil size={14} />
                  עריכה
                </button>
              )}
              <button
                onClick={handlePrint}
                disabled={printLoading || loading}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-50 transition-colors"
              >
                {printLoading
                  ? <span className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <Printer size={14} />
                }
                הדפסה
              </button>
              <button onClick={onClose}
                className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
            {loading ? (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" style={{ width: `${70 - i * 10}%` }} />
                ))}
              </div>
            ) : !order ? (
              <p className="text-center text-sm text-gray-400 py-8">שגיאה בטעינת ההזמנה</p>
            ) : (
              <>
                {/* Unpaid warning */}
                {order.status === 'completed' && !order.is_paid && (
                  <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 font-semibold text-sm">
                    <AlertTriangle size={16} className="shrink-0" />
                    הזמנה טרם שולמה!
                  </div>
                )}

                {/* Customer + Paid + Status */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs text-gray-400 mb-1">לקוח</p>
                    <button
                      onClick={() => { onClose(); navigate(`/customers/${order.customer_id}`); }}
                      className="flex items-center gap-1.5 font-semibold text-primary-600 dark:text-primary-400 hover:underline text-sm"
                    >
                      <User size={14} />
                      {order.customer_name}
                      <ExternalLink size={11} />
                    </button>
                    {order.customer_city && (
                      <p className="text-xs text-gray-400 mt-0.5">{order.customer_city}</p>
                    )}
                  </div>

                  <div className="flex items-start gap-3 shrink-0">
                    <div>
                      <p className="text-xs text-gray-400 mb-1">תשלום</p>
                      <button
                        onClick={togglePaid}
                        disabled={paidSaving}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 text-sm font-medium transition-colors disabled:opacity-60 ${order.is_paid ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20' : 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-emerald-400 hover:text-emerald-600'}`}
                      >
                        {order.is_paid ? <><CheckCircle2 size={14} /> שולם</> : <><Circle size={14} /> לא שולם</>}
                      </button>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">סטטוס</p>
                      <select
                        value={order.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        disabled={statusSaving}
                        className="text-sm px-3 py-1.5 rounded-lg border-2 font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 disabled:opacity-60"
                        style={{ borderColor: STATUS_COLORS[order.status], color: STATUS_COLORS[order.status] }}
                      >
                        <option value="new">הזמנה חדשה</option>
                        <option value="sent">נשלחה</option>
                        <option value="completed">הסתיימה</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <p className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                    <Package size={12} /> פירוט פריטים
                  </p>
                  {items.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">לא הוזנו פריטים</p>
                  ) : (
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 dark:bg-gray-700/50 text-xs text-gray-500 dark:text-gray-400">
                            <th className="px-3 py-2 text-right font-medium">תיאור</th>
                            <th className="px-3 py-2 text-center font-medium w-16">כמות</th>
                            <th className="px-3 py-2 text-left font-medium w-24">מחיר</th>
                            <th className="px-3 py-2 text-left font-medium w-24">סה"כ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item, i) => {
                            const qty = parseInt(item.quantity) || 1;
                            const price = parseFloat(item.price) || 0;
                            return (
                              <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
                                <td className="px-3 py-2.5 text-gray-800 dark:text-gray-200">{item.description || '—'}</td>
                                <td className="px-3 py-2.5 text-center text-gray-500">{qty}</td>
                                <td className="px-3 py-2.5 text-left text-gray-600 dark:text-gray-300 font-mono text-xs">
                                  {price > 0 ? `₪${price.toLocaleString()}` : '—'}
                                </td>
                                <td className="px-3 py-2.5 text-left font-medium text-gray-800 dark:text-gray-200 font-mono text-xs">
                                  {price > 0 ? `₪${(price * qty).toLocaleString()}` : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Totals */}
                {(order.total_amount || computedTotal > 0) && (() => {
                  const total = order.total_amount ? Number(order.total_amount) : computedTotal;
                  const discount = computedTotal > 0 && Number(order.total_amount) > 0 && computedTotal > Number(order.total_amount)
                    ? computedTotal - Number(order.total_amount)
                    : 0;
                  return (
                    <div className="flex justify-end">
                      <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg overflow-hidden min-w-[220px]">
                        {discount > 0 && (
                          <>
                            <div className="flex justify-between px-4 py-2 text-sm border-b border-primary-100 dark:border-primary-800/40">
                              <span className="text-gray-500 dark:text-gray-400">סכום פריטים</span>
                              <span className="text-gray-700 dark:text-gray-200 font-mono text-xs" dir="ltr">
                                ₪{computedTotal.toLocaleString('he-IL', { maximumFractionDigits: 0 })}
                              </span>
                            </div>
                            <div className="flex justify-between px-4 py-2 text-sm border-b border-primary-100 dark:border-primary-800/40">
                              <span className="text-red-600 dark:text-red-400">הנחה</span>
                              <span className="text-red-600 dark:text-red-400 font-mono text-xs" dir="ltr">
                                ‎-₪{discount.toLocaleString('he-IL', { maximumFractionDigits: 0 })}
                              </span>
                            </div>
                          </>
                        )}
                        <div className="flex items-center justify-between px-4 py-3 gap-6">
                          <span className="text-sm text-primary-700 dark:text-primary-300 font-medium">סה"כ לתשלום</span>
                          <span className="text-xl font-bold text-primary-700 dark:text-primary-300 font-mono" dir="ltr">
                            ₪{total.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Notes */}
                {order.notes && (
                  <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-3">
                    <p className="text-xs text-amber-600 dark:text-amber-400 font-medium mb-1">הערות</p>
                    <p className="text-sm text-amber-800 dark:text-amber-200 whitespace-pre-wrap">{order.notes}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {printPreview && (
        <DocumentPreview doc={printPreview} onClose={() => setPrintPreview(null)} />
      )}

      {showEdit && order && (
        <OrderModal
          order={order}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); loadOrder(); onStatusChanged?.(); }}
        />
      )}
    </>
  );
}
